import { Button } from "@/components/ui/Button";
import {
  newOnlineBookingMinStayPeriod,
  type OnlineBookingMinStayPeriod,
} from "@/lib/onlineBooking";

type Props = {
  periods: OnlineBookingMinStayPeriod[];
  onChange: (periods: OnlineBookingMinStayPeriod[]) => void;
  idPrefix?: string;
};

export function OnlineBookingMinStayPeriodsEditor({
  periods,
  onChange,
  idPrefix = "min-stay",
}: Props) {
  const updatePeriod = (
    periodId: string,
    updater: (period: OnlineBookingMinStayPeriod) => OnlineBookingMinStayPeriod,
  ) => {
    onChange(periods.map((period) => (period.id === periodId ? updater(period) : period)));
  };

  return (
    <div className="space-y-4">
      {periods.length === 0 ? (
        <p className="text-sm text-label-secondary">Keine Zeiträume definiert.</p>
      ) : (
        <ul className="space-y-4">
          {periods.map((period) => (
            <li key={period.id} className="space-y-3 rounded-lg border border-separator p-3">
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-sm">
                  Bezeichnung
                  <input
                    type="text"
                    className="eh-input"
                    value={period.name}
                    onChange={(e) =>
                      updatePeriod(period.id, (current) => ({ ...current, name: e.target.value }))
                    }
                    placeholder="z. B. Hauptsaison"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  Mindestnächte
                  <input
                    type="number"
                    className="eh-input w-28"
                    min={1}
                    max={365}
                    value={period.min_nights}
                    onChange={(e) =>
                      updatePeriod(period.id, (current) => ({
                        ...current,
                        min_nights: Math.max(1, Number(e.target.value) || 1),
                      }))
                    }
                  />
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => onChange(periods.filter((item) => item.id !== period.id))}
                >
                  Entfernen
                </Button>
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-label">Zeitraum-Typ</legend>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name={`${idPrefix}-mode-${period.id}`}
                    checked={period.mode === "recurring"}
                    onChange={() =>
                      updatePeriod(period.id, (current) => ({
                        ...newOnlineBookingMinStayPeriod("recurring"),
                        id: current.id,
                        name: current.name,
                        min_nights: current.min_nights,
                      }))
                    }
                  />
                  <span>
                    <strong>Jährlich wiederkehrend</strong> – identisch im nächsten Jahr (z. B.
                    01.04. bis 30.09.)
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name={`${idPrefix}-mode-${period.id}`}
                    checked={period.mode === "fixed"}
                    onChange={() =>
                      updatePeriod(period.id, (current) => ({
                        ...newOnlineBookingMinStayPeriod("fixed"),
                        id: current.id,
                        name: current.name,
                        min_nights: current.min_nights,
                      }))
                    }
                  />
                  <span>
                    <strong>Festes Kalenderjahr</strong> – exakte Start- und Enddaten
                  </span>
                </label>
              </fieldset>
              {period.mode === "recurring" ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm">
                    Von (Tag.Monat)
                    <div className="flex gap-2">
                      <input
                        type="number"
                        className="eh-input w-20"
                        min={1}
                        max={31}
                        value={period.start_day ?? ""}
                        onChange={(e) =>
                          updatePeriod(period.id, (current) => ({
                            ...current,
                            start_day: Number(e.target.value) || null,
                          }))
                        }
                        aria-label="Starttag"
                      />
                      <input
                        type="number"
                        className="eh-input w-20"
                        min={1}
                        max={12}
                        value={period.start_month ?? ""}
                        onChange={(e) =>
                          updatePeriod(period.id, (current) => ({
                            ...current,
                            start_month: Number(e.target.value) || null,
                          }))
                        }
                        aria-label="Startmonat"
                      />
                    </div>
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    Bis (Tag.Monat)
                    <div className="flex gap-2">
                      <input
                        type="number"
                        className="eh-input w-20"
                        min={1}
                        max={31}
                        value={period.end_day ?? ""}
                        onChange={(e) =>
                          updatePeriod(period.id, (current) => ({
                            ...current,
                            end_day: Number(e.target.value) || null,
                          }))
                        }
                        aria-label="Endtag"
                      />
                      <input
                        type="number"
                        className="eh-input w-20"
                        min={1}
                        max={12}
                        value={period.end_month ?? ""}
                        onChange={(e) =>
                          updatePeriod(period.id, (current) => ({
                            ...current,
                            end_month: Number(e.target.value) || null,
                          }))
                        }
                        aria-label="Endmonat"
                      />
                    </div>
                  </label>
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm">
                    Von
                    <input
                      type="date"
                      className="eh-input"
                      value={period.start_date ?? ""}
                      onChange={(e) =>
                        updatePeriod(period.id, (current) => ({
                          ...current,
                          start_date: e.target.value || null,
                        }))
                      }
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    Bis
                    <input
                      type="date"
                      className="eh-input"
                      value={period.end_date ?? ""}
                      onChange={(e) =>
                        updatePeriod(period.id, (current) => ({
                          ...current,
                          end_date: e.target.value || null,
                        }))
                      }
                    />
                  </label>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => onChange([...periods, newOnlineBookingMinStayPeriod("recurring")])}
        >
          Jährlichen Zeitraum hinzufügen
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => onChange([...periods, newOnlineBookingMinStayPeriod("fixed")])}
        >
          Zeitraum mit festem Jahr hinzufügen
        </Button>
      </div>
    </div>
  );
}
